import React, { FC, useCallback, useState } from 'react';
import { Button, Card, CardBody, CardText, CardTitle, Col, Row } from 'reactstrap';
import { useTranslation } from '../translations';
import { useConfig, useConvention } from '../utils';
import { MaterialIcon, TermsLoader } from '.';

export enum AgeCheckResult {
  Adult,
  Teen,
  Underage,
}

interface AgeCheckProps {
  readonly onSelect: (res: AgeCheckResult) => void;
}

function yearsAgo(date: Date, years: number): Date {
  const ret = new Date(date);
  ret.setFullYear(date.getFullYear() - years);
  return ret;
}

export const AgeCheck: FC<AgeCheckProps> = ({ onSelect }) => {
  const convention = useConvention();

  if (convention.identityVerification !== 'none') {
    return <AgeRestrictionBarrier onSelect={onSelect} />;
  }

  return <AllAgeCon onSelect={onSelect} />;
};

const AgeRestrictionBarrier: FC<AgeCheckProps> = ({ onSelect }) => {
  const { ts } = useTranslation();
  const [underage, setUnderage] = useState(false);
  const selectAdult = useCallback(() => {
    onSelect(AgeCheckResult.Adult);
  }, [onSelect]);

  const selectUnderage = useCallback(() => {
    setUnderage(true);
    onSelect(AgeCheckResult.Underage);
  }, [onSelect]);

  const { startAt, minorAgeThreshold } = useConvention();
  const config = useConfig();
  const date = startAt.getUTCDate();
  const month = startAt.toLocaleDateString(ts('common.locale_code'), {
    month: 'long',
    timeZone: 'UTC',
  });

  const year = startAt.getUTCFullYear();

  if (underage) {
    return (
      <Card>
        <CardBody className="text-center">
          <MaterialIcon large name="error" type="danger" />
          <CardTitle>{ts('age_check.we_are_sorry')}</CardTitle>
          <CardText>
            <p>{ts('age_check.this_is_a_minimum_age_convention', minorAgeThreshold)}</p>
            <p>
              {ts('age_check.if_become_over_minimum_age', minorAgeThreshold)}{' '}
              <strong>{config.contact.email.registration}</strong>.
            </p>
          </CardText>
          <Button
            block
            color="primary"
            onClick={() => {
              setUnderage(false);
            }}
            style={{ marginTop: '20px' }}
          >
            {ts('common.go_back')}
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="text-center">
        <CardTitle>{ts('age_check.age_check')}</CardTitle>
        <CardText>{ts('age_check.confirm_minimum_age', minorAgeThreshold)}</CardText>
        <Row className="justify-content-center">
          <Col className="mb-10" lg={8} onClick={selectAdult} xs={12}>
            <Button block color="primary" id="age-option-adult">
              {ts(
                'age_check.age_declaration_over',
                month,
                date,
                year - minorAgeThreshold,
                minorAgeThreshold,
              )}
            </Button>
          </Col>
          <Col className="mb-10" lg={8} onClick={selectUnderage} xs={12}>
            <Button block color="primary" id="age-option-teen">
              {ts(
                'age_check.age_declaration_under',
                month,
                date,
                year - minorAgeThreshold,
                minorAgeThreshold - 1,
              )}
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const AllAgeCon: FC<AgeCheckProps> = ({ onSelect }) => {
  const { ts } = useTranslation();
  const [underage, setUnderage] = useState(false);
  const selectAdult = useCallback(() => {
    onSelect(AgeCheckResult.Adult);
  }, [onSelect]);

  const selectTeen = useCallback(() => {
    onSelect(AgeCheckResult.Teen);
  }, [onSelect]);

  const selectUnderage = useCallback(() => {
    setUnderage(true);
    onSelect(AgeCheckResult.Underage);
  }, [onSelect]);

  const { startAt, timeZone, childAgeThreshold, minorAgeThreshold } = useConvention();

  const dateFormatter = new Intl.DateTimeFormat(ts('common.locale_code'), {
    day: 'numeric',
    month: 'long',
    timeZone,
    year: 'numeric',
  });

  if (underage) {
    return <UnderAgeWarning />;
  }

  let minorCutOffDate = yearsAgo(startAt, minorAgeThreshold);
  let childCutOffDate = yearsAgo(startAt, childAgeThreshold);
  if (childCutOffDate > yearsAgo(new Date(), 13)) {
    childCutOffDate = yearsAgo(new Date(), 13);
  }

  if (minorCutOffDate > childCutOffDate) {
    minorCutOffDate = childCutOffDate;
    minorCutOffDate.setDate(minorCutOffDate.getDate() - 1);
  }

  const cutoffDateStr = dateFormatter.format(childCutOffDate);
  return (
    <Card>
      <CardBody className="text-center">
        <CardTitle>{ts('age_check.age_check')}</CardTitle>
        <CardText>{ts('age_check.confirm_minimum_age', childAgeThreshold)}</CardText>
        <Row className="justify-content-center">
          <Col className="mb-10" lg={8} onClick={selectAdult} xs={12}>
            <Button block color="primary" id="age-option-adult">
              {ts(
                'age_check.age_declaration__adult',
                dateFormatter.format(minorCutOffDate),
                minorAgeThreshold,
              )}
            </Button>
          </Col>
          {childAgeThreshold < minorAgeThreshold && (
            <Col className="mb-10" lg={8} onClick={selectTeen} xs={12}>
              <Button block color="primary" id="age-option-teen">
                {ts(
                  'age_check.age_declaration__minor',
                  cutoffDateStr,
                  childAgeThreshold,
                  minorAgeThreshold,
                )}
              </Button>
            </Col>
          )}
          <Col className="mb-10" lg={8} onClick={selectUnderage} xs={12}>
            <Button block color="primary" id="age-option-underage">
              {ts('age_check.age_declaration__child', cutoffDateStr, childAgeThreshold)}
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const UnderAgeWarning: FC = () => {
  const { ts } = useTranslation();
  return (
    <Card>
      <CardBody className="text-center">
        <MaterialIcon large name="error" type="danger" />
        <CardTitle>{ts('age_check.we_are_sorry')}</CardTitle>
        <CardText>
          <TermsLoader name="childRegistrationError" />
        </CardText>
      </CardBody>
    </Card>
  );
};
